import * as React from "react";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Text,
  IconButton,
  List,
  PrimaryButton,
  Separator,
  Stack,
  TextField,
  ITextField,
  Modal,
} from "@fluentui/react";

import { AppStateContext } from "../../state/AppProvider";
import { GroupedChatHistory } from "./ChatHistoryList";

import styles from "./ChatHistoryPanel.module.css";
import { useBoolean } from "@fluentui/react-hooks";
import {
  ChatHistoryLoadingState,
  Conversation,
  SingleConversation,
} from "../../api/models";
import { historyDelete, historyRead, historyRename } from "../../api";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { ToastContainer, toast } from "react-toastify";
 

interface ChatHistoryListItemCellProps {
  item?: Conversation;
  onSelect: (item: Conversation | null) => void;
}

interface ChatHistoryListItemGroupsProps {
  groupedChatHistory: GroupedChatHistory[];
}

const formatMonth = (month: string) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  const [monthName, yearString] = month.split(" ");
  const year = parseInt(yearString);

  if (year === currentYear) {
    return monthName;
  } else {
    return month;
  }
};

export const ChatHistoryListItemCell: React.FC<
  ChatHistoryListItemCellProps
> = ({ item, onSelect }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [edit, setEdit] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [hideDeleteDialog, { toggle: toggleDeleteDialog }] = useBoolean(true);
  const [errorDelete, setErrorDelete] = useState(false);
  const [renameLoading, setRenameLoading] = useState(false);
  const [errorRename, setErrorRename] = useState<string | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [textFieldFocused, setTextFieldFocused] = useState(false);
  const textFieldRef = useRef<ITextField | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const appStateContext = React.useContext(AppStateContext);
  const isSelected = item?.id === appStateContext?.state.currentChat?.id;
  const dialogContentProps = {
    type: DialogType.close,
    title: "Are you sure you want to delete this item?",
    closeButtonAriaLabel: "Close",
    subText: "The history of this chat session will permanently removed.",
  };

  const modalProps = {
    titleAriaId: "labelId",
    subtitleAriaId: "subTextId",
    isBlocking: true,
    styles: { main: { maxWidth: 450 } },
  };

  if (!item) {
    return null;
  }

  



  useEffect(() => {
    if (textFieldFocused && textFieldRef.current) {
      textFieldRef.current.focus();
      setTextFieldFocused(false);
    }
  }, [textFieldFocused]);

 

  useEffect(() => {
    if (appStateContext?.state.currentChat?.id !== item?.id) {
      setEdit(false);
      setEditTitle("");
    }
  }, [appStateContext?.state.currentChat?.id, item?.id]);

    const onDelete = async () => {
  setIsDeleting(true);
 
  try {
    const response = await historyDelete(item.id);
 
    if (!response.ok) {
      setErrorDelete(true);
      toast.error('Failed to delete the item.');
      setTimeout(() => {
        setErrorDelete(false);
      }, 5000);
    } else {
      appStateContext?.dispatch({
        type: "DELETE_CHAT_ENTRY",
        payload: item.id,
      });
      toast.success('Item deleted successfully!',{
         theme: "light",
      });
      toggleDeleteDialog();
    }
  } catch (error) {
    toast.error('An unexpected error occurred.');
    setErrorDelete(true);
  } finally {
    setIsDeleting(false);
  }
};
 
 

  // const onDelete = async () => {
  //   setIsDeleting(true);
  //   const response = await historyDelete(item.id);

  //   if (!response.ok) {
  //     setErrorDelete(true);
  //     setIsDeleting(false);
  //     setTimeout(() => {
  //       setErrorDelete(false);
  //     }, 5000);
  //   } else {
  //     appStateContext?.dispatch({
  //       type: "DELETE_CHAT_ENTRY",
  //       payload: item.id,
  //     });
  //     toggleDeleteDialog();
  //     setIsDeleting(false);
  //   }
  // };

  const onEdit = () => {
    setEdit(true);
    setTextFieldFocused(true);
    setEditTitle(item?.title);
  };
  const handleSelectItem = () => {
    onSelect(item);
    appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: item });
  };

  const truncatedTitle =
    item?.title?.length > 28
      ? `${item.title.substring(0, 28)} ...`
      : item.title;

  const handleSaveEdit = async (e: any) => {
    e.preventDefault();
    if (errorRename || renameLoading) {
      return;
    }
    if (editTitle == item.title) {
      setErrorRename("Enter a new title to proceed.");
      setTimeout(() => {
        setErrorRename(undefined);
        setTextFieldFocused(true);
        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
      }, 5000);
      return;
    }
    setRenameLoading(true);
    let response = await historyRename(item.id, editTitle);
    if (!response.ok) {
      setErrorRename("Error: could not rename item");
      setTimeout(() => {
        setTextFieldFocused(true);
        setErrorRename(undefined);
        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
      }, 5000);
    } else {
      setRenameLoading(false);
      setEdit(false);
      appStateContext?.dispatch({
        type: "UPDATE_CHAT_TITLE",
        payload: { ...item, title: editTitle } as Conversation,
      });
      setEditTitle("");
    }
  };

  const chatHistoryTitleOnChange = (e: any) => {
    setEditTitle(e.target.value);
  };

  const cancelEditTitle = () => {
    setEdit(false);
    setEditTitle("");
  };

  const handleKeyPressEdit = (e: any) => {
    if (e.key === "Enter") {
      return handleSaveEdit(e);
    }
    if (e.key === "Escape") {
      cancelEditTitle();
      return;
    }
  };

  const EditIconSvg: React.FC<React.SVGProps<SVGSVGElement>> = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 10 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.24997 2.75003H1.74997C1.48475 2.75003 1.2304 2.85539 1.04286 3.04292C0.855326 3.23046 0.749969 3.48481 0.749969 3.75003V8.25003C0.749969 8.51525 0.855326 8.7696 1.04286 8.95714C1.2304 9.14467 1.48475 9.25003 1.74997 9.25003H6.24997C6.51519 9.25003 6.76954 9.14467 6.95708 8.95714C7.14461 8.7696 7.24997 8.51525 7.24997 8.25003V7.75003"
        stroke="#414143"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M8.94247 2.54252C9.13939 2.3456 9.25002 2.07851 9.25002 1.80002C9.25002 1.52153 9.13939 1.25445 8.94247 1.05752C8.74555 0.8606 8.47846 0.749969 8.19997 0.749969C7.92148 0.749969 7.65439 0.8606 7.45747 1.05752L3.24997 5.25002V6.75002H4.74997L8.94247 2.54252Z"
        stroke="#414143"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M6.74997 1.75003L8.24997 3.25003"
        stroke="#414143"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );

  const DeleteIconSvg: React.FC<React.SVGProps<SVGSVGElement>> = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 10 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.5 2H10V3H9V9.5C9 9.77615 8.77615 10 8.5 10H1.5C1.22386 10 1 9.77615 1 9.5V3H0V2H2.5V0.5C2.5 0.22386 2.72386 0 3 0H7C7.27615 0 7.5 0.22386 7.5 0.5V2ZM8 3H2V9H8V3ZM3.5 4.5H4.5V7.5H3.5V4.5ZM5.5 4.5H6.5V7.5H5.5V4.5ZM3.5 1V2H6.5V1H3.5Z"
        fill="#414143"
      />
    </svg>
  );

  const ConfirmEdit: React.FC<React.SVGProps<SVGSVGElement>> = () => (
    <svg
      stroke="currentColor"
      fill="currentColor"
      stroke-width="0"
      viewBox="0 0 448 512"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"></path>
    </svg>
  );

  const CancelEditSvg: React.FC<React.SVGProps<SVGSVGElement>> = () => (
    <svg
      stroke="currentColor"
      fill="currentColor"
      stroke-width="0"
      viewBox="0 0 24 24"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path fill="none" className="canceledit-1" d="M0 0h24v24H0z"></path>
      <path
        className="canceledit-2"
        d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
      ></path>
    </svg>
  );

  return (
    
    <Stack
      key={item.id}
      tabIndex={0}
      aria-label="chat history item"
      className={`${styles.itemCell} ${isSelected?styles.itemCell_selected:""}`}
      onClick={() => handleSelectItem()}
      onKeyDown={(e) =>
        e.key === "Enter" || e.key === " " ? handleSelectItem() : null
      }
      verticalAlign="center"
      // horizontal
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      styles={{
        root: {
          // backgroundColor: isSelected ? "--bg-color-8" : "transparent",          
        },
      }}
    >
      
      {edit ? (
        <>
          <Stack.Item style={{ width: "100%" }}>
            <form
              aria-label="edit title form"
              onSubmit={(e) => handleSaveEdit(e)}
              className="editChatForm"
              style={{flexDirection:"column",gap:8}}
            >
              <Stack horizontal verticalAlign={"start"}>
                <Stack.Item  className="editInputChatFormWrapper">
                  <TextField
                    componentRef={textFieldRef}
                    autoFocus={textFieldFocused}
                    value={editTitle}
                    placeholder={item.title}
                    onChange={chatHistoryTitleOnChange}
                    onKeyDown={handleKeyPressEdit}
                    // errorMessage={errorRename}
                    disabled={errorRename ? true : false}                   
                     styles={{
                          fieldGroup: {
                            borderRadius: "8px",                            
                            background: "transparent!important",
                            border:"1px solid rgb(71 69 69)"
                          },
                          field: {
                            color: "#fff", 
                            background: "transparent!important",                          
                          },
                        }}
                  />
                </Stack.Item>
                {editTitle && (
                  <Stack.Item>
                    <Stack
                      aria-label="action button group"
                      horizontal
                      verticalAlign={"center"}
                    >
                      <button
                        className="confirmEdit"
                        disabled={errorRename !== undefined}
                        onKeyDown={(e) =>
                          e.key === " " || e.key === "Enter"
                            ? handleSaveEdit(e)
                            : null
                        }
                        onClick={(e) => handleSaveEdit(e)}
                        aria-label="confirm new title"
                      >
                        <i>
                          <ConfirmEdit className={styles.itemButton} />
                        </i>
                      </button>

                      <button
                        className="cancelEdit"
                        disabled={errorRename !== undefined}
                        onKeyDown={(e) =>
                          e.key === " " || e.key === "Enter"
                            ? cancelEditTitle()
                            : null
                        }
                        onClick={() => cancelEditTitle()}
                        aria-label="cancel edit title"
                      >
                        <i>
                          <CancelEditSvg className={styles.itemButton} />
                        </i>
                      </button>
                      {/* <IconButton role='button'  />
                                    <IconButton role='button'  iconProps={{iconName: 'Cancel'}} styles={{ root: { color: 'red', marginLeft: '5px' } }} /> */}
                    </Stack>
                  </Stack.Item>
                )}
              </Stack>
              {errorRename && (
                <Text
                  role="alert"
                  aria-label={errorRename}
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: "#db2128",
                  }}
                >
                  {errorRename}
                </Text>
              )}
            </form>
          </Stack.Item>
        </>
      ) : (
        <>
          <Stack horizontal verticalAlign={"center"} style={{ width: "100%" }}>
            
            <div className={styles.chatTitle}>
              <i className="mr-2">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 8 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.25 0.625C0.25 0.525544 0.289509 0.430161 0.359835 0.359835C0.430161 0.289509 0.525544 0.25 0.625 0.25H7.375C7.47446 0.25 7.56984 0.289509 7.64017 0.359835C7.71049 0.430161 7.75 0.525544 7.75 0.625C7.75 0.724456 7.71049 0.819839 7.64017 0.890165C7.56984 0.960491 7.47446 1 7.375 1H0.625C0.525544 1 0.430161 0.960491 0.359835 0.890165C0.289509 0.819839 0.25 0.724456 0.25 0.625ZM0.25 2.5C0.25 2.40054 0.289509 2.30516 0.359835 2.23484C0.430161 2.16451 0.525544 2.125 0.625 2.125H7.375C7.47446 2.125 7.56984 2.16451 7.64017 2.23484C7.71049 2.30516 7.75 2.40054 7.75 2.5C7.75 2.59946 7.71049 2.69484 7.64017 2.76516C7.56984 2.83549 7.47446 2.875 7.375 2.875H0.625C0.525544 2.875 0.430161 2.83549 0.359835 2.76516C0.289509 2.69484 0.25 2.59946 0.25 2.5ZM0.25 4.375C0.25 4.27554 0.289509 4.18016 0.359835 4.10983C0.430161 4.03951 0.525544 4 0.625 4H4.375C4.47446 4 4.56984 4.03951 4.64017 4.10983C4.71049 4.18016 4.75 4.27554 4.75 4.375C4.75 4.47446 4.71049 4.56984 4.64017 4.64017C4.56984 4.71049 4.47446 4.75 4.375 4.75H0.625C0.525544 4.75 0.430161 4.71049 0.359835 4.64017C0.289509 4.56984 0.25 4.47446 0.25 4.375Z"
                    fill="currentColor"
                  ></path>
                </svg>
              </i>{" "}
              {truncatedTitle}
            </div>
            {(isSelected || isHovered) && (
              <Stack horizontal horizontalAlign="end">
                <button
                  className="history_edit"
                  onClick={(e) => {
                    e.stopPropagation(); 
                    onEdit(); 
                  }}
                  onKeyDown={(e) => {
                    if (e.key === " ") {
                      e.stopPropagation(); 
                      onEdit();
                    }
                  }}
                >
                  <i>
                    <EditIconSvg className={styles.itemButton} />
                  </i>
                </button>
                <button
                  className="history_delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDeleteDialog();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === " ") {
                      e.stopPropagation();
                      toggleDeleteDialog();
                    }
                  }}
                >
                  <i>
                    <DeleteIconSvg className={styles.itemButton} />
                  </i>
                </button>
              </Stack>
            )}
          </Stack>
        </>
      )}
      {errorDelete && (
        <Text
          styles={{
            root: { color: "red", marginTop: 5, fontSize: 14 },
          }}
        >
          Error: could not delete item
        </Text>
      )}

      {!hideDeleteDialog && (
        <Modal
          isOpen={true}
          // onClose={toggleDeleteDialog}
          aria-labelledby="feedback-modal-title"
          aria-describedby="feedback-modal-description"
          className="chatDeleteModal"
          // style={{
          //   display: 'flex',
          //   alignItems: 'center',
          //   justifyContent: 'center',
          // }}
        >
          <div
            className={`${styles.modal_content} ${styles.modal_content_chatdelete}`}
          >
            <div className="d-flex align-items-center justify-content-between">
              <h3 id="feedback-modal-title" className={styles.modalTitle}>
                Are you sure you want to delete this item?
              </h3>
              <button
                onClick={toggleDeleteDialog}
                className={styles.chat_closeBtn}
              >
                X
              </button>
            </div>
            <p className={styles.modalTitle_subText}>
              The history of the chat session will permanently removed.
            </p>
            <div style={{ marginTop: "24px", textAlign: "right" }}>
              {isDeleting ? (
                // <Spinner
                //   size={SpinnerSize.medium}
                //   label="Deleting..."
                //   labelPosition="right"
                //   styles={{
                //     circle: { color: "white" },
                //     label: { color: "white" },
                //   }}
                // />
                        <div style={{ display: "flex", alignItems: "center",justifyContent: "center"   }}>
                      <div className="chatLoaderModal" />
                      <span className="submitting">Deleting...</span>
                    </div>
              ) : (
                <>
                  <button
                    className={styles.submitBtnChat}
                    onClick={onDelete}
                    disabled={isDeleting}
                  >
                    Delete
                  </button>
                  <button
                    className={styles.cancelBtnDeleteChat}
                    onClick={toggleDeleteDialog}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* <Dialog
                hidden={hideDeleteDialog}
                onDismiss={toggleDeleteDialog}
                dialogContentProps={dialogContentProps}
                modalProps={modalProps}
            >
                <DialogFooter>
                <PrimaryButton onClick={onDelete} text="Delete" />
                <DefaultButton onClick={toggleDeleteDialog} text="Cancel" />
                </DialogFooter>
            </Dialog> */}
    </Stack>
  );
};

export const ChatHistoryListItemGroups: React.FC<
  ChatHistoryListItemGroupsProps
> = ({ groupedChatHistory }) => {
  const [, setSelectedItem] = React.useState<Conversation | null>(null);
  const appStateContext = React.useContext(AppStateContext);
  const handleSelectHistory = (item?: Conversation) => {
    if (item) {
      setSelectedItem(item);
      fetchMessages(item);
    }
  };


 

React.useEffect(() => {}, [appStateContext?.state.singleCurrentChat]);

const fetchMessages = async (item?: any) => {
  if (item?.id) {
    appStateContext?.dispatch({
      type: "SET_FETCHING_MESSAGES",
      payload: true,
    });

    try {
      let response = await historyRead(item?.id);
      const newConversation = {
        id: item.id,
        title: item.title,
        date: item.date,
        messages: response,
      };
     
      appStateContext?.dispatch({
        type: "UPDATE_CURRENT_CHAT",
        payload: newConversation,
      });

     
      setTimeout(() => {
        appStateContext?.dispatch({
          type: "SET_FETCHING_MESSAGES",
          payload: false,
        });
      }, 1000); 
    } catch (error) {
      console.error("Error fetching messages:", error);
      appStateContext?.dispatch({
        type: "SET_FETCHING_MESSAGES",
        payload: false,
      });
    }
  } else {
  //  console.log("No data received from historyRead");
  }
};


  const onRenderCell = (item?: Conversation) => {


    return (
      <ChatHistoryListItemCell
        item={item}
        onSelect={() => handleSelectHistory(item)}
      />
    );
  };

  const location = useLocation();

  const isProfilePage = location.pathname === "/my-profile";

  return (
    // <div className={styles.listContainer}>
    <div
      className={`${styles.listContainer} ${
        isProfilePage ? styles.listContainer_inactive : ""
      }`}
    >
      {groupedChatHistory.map(
        (group) =>
          group.entries.length > 0 && (
            <Stack
              horizontalAlign="start"
              verticalAlign="center"
              key={group.month}
              className={styles.chatGroup}
              aria-label={`chat history group: ${group.month}`}
            >
              <Stack aria-label={group.month} className={styles.chatMonth}>
                {formatMonth(group.month)}
              </Stack>
              <List
                aria-label={`chat history list`}
                items={group.entries}
                onRenderCell={onRenderCell}
                className={styles.chatList}
              />
              <Separator 
                styles={{
                  root: {
                    width: "100%",
                    position: "relative",
                    "::before": {
                      backgroundColor: "var(--chat-his-sep)!important",
                    },
                  },
                }}
              />
            </Stack>
          )
      )}
    </div>
  );
};
